import type { PropsWithChildren } from "react";

import { TasksTestID, TextTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import ModalFooter from "@/ui/composed/modal/ModalFooter";
import ModalTitleHeader from "@/ui/composed/modal/ModalTitleHeader";
import { FAIconName } from "@/ui/core";
import ModalLayout from "@/ui/layout/ModalLayout";
import { joinTestIDs } from "@/utils/string.util";

const baseTestID = TasksTestID.TaskDetails;

enum FooterActionLabel {
  Assessment = "Start survey",
  Complete = "Complete",
  Continue = "Continue",
  LogWeight = "Add to log",
  SaveCard = "Save Card",
  SignDocument = "Sign document",
}

const RightButtonIcon: Record<FooterActionLabel, FAIconName> = {
  [FooterActionLabel.Assessment]: FAIconName.ArrowRight,
  [FooterActionLabel.Complete]: FAIconName.Check,
  [FooterActionLabel.Continue]: FAIconName.ArrowRight,
  [FooterActionLabel.LogWeight]: FAIconName.Check,
  [FooterActionLabel.SaveCard]: FAIconName.Check,
  [FooterActionLabel.SignDocument]: FAIconName.ExternalLink,
};

interface TaskDetailsWrapperProps extends PropsWithChildren {
  footerRightButtonLabel: FooterActionLabel;
  isLoading: boolean;
  isRightButtonDisabled?: boolean;
  onFooterLeftButtonPress?: () => Promise<void> | void;
  onFooterRightButtonPress: () => Promise<void> | void;
}

const TaskDetailsModalWrapper = ({
  children,
  footerRightButtonLabel = FooterActionLabel.Assessment,
  isLoading,
  isRightButtonDisabled = false,
  onFooterLeftButtonPress,
  onFooterRightButtonPress,
}: TaskDetailsWrapperProps) => {
  const shouldShowRightIcon =
    footerRightButtonLabel === FooterActionLabel.Assessment ||
    footerRightButtonLabel === FooterActionLabel.Continue ||
    footerRightButtonLabel === FooterActionLabel.SignDocument;

  const handleModalDismiss = () => {
    if (onFooterLeftButtonPress) {
      void onFooterLeftButtonPress();
    } else {
      router.dismiss();
    }
  };

  return (
    <ModalLayout
      isPaddedHorizontally
      renderFooter={() => (
        <ModalFooter
          isLoading={isLoading}
          isRightButtonDisabled={isRightButtonDisabled}
          leftButtonType="cancel"
          onLeftButtonPress={handleModalDismiss}
          onRightButtonPress={onFooterRightButtonPress}
          rightButtonLabel={footerRightButtonLabel}
          rightButtonLeftIcon={
            shouldShowRightIcon
              ? undefined
              : RightButtonIcon[footerRightButtonLabel]
          }
          rightButtonRightIcon={
            shouldShowRightIcon
              ? RightButtonIcon[footerRightButtonLabel]
              : undefined
          }
          testID={baseTestID}
        />
      )}
      renderHeader={() => (
        <ModalTitleHeader
          navigationType="close"
          onDismiss={handleModalDismiss}
          testID={joinTestIDs(baseTestID, TextTestID.Title)}
          title="To do"
        />
      )}
    >
      {children}
    </ModalLayout>
  );
};

export default TaskDetailsModalWrapper;
export { FooterActionLabel };
